import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import {
  type Client,
  type ClientUpdate,
  type EnergyType,
  clientsApi,
} from "@/api/clients";
import { useCanEdit } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { MetersSection } from "./MetersSection";
import { TariffsSection } from "./TariffsSection";

interface ClientDetailProps {
  clientId: string;
  onDeactivated: () => void;
}

export function ClientDetail({ clientId, onDeactivated }: ClientDetailProps) {
  const { t } = useTranslation();
  const canEdit = useCanEdit();
  const qc = useQueryClient();

  const [tab, setTab] = useState<EnergyType>("electricity");

  const detail = useQuery({
    queryKey: ["clients", "list"],
    queryFn: () => clientsApi.list(true),
    select: (clients) => clients.find((c) => c.id === clientId) ?? null,
  });

  const client: Client | null = detail.data ?? null;

  const deactivate = useMutation({
    mutationFn: () => clientsApi.deactivate(clientId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      onDeactivated();
    },
  });

  if (detail.isLoading || !client) {
    return (
      <Card>
        <CardContent className="py-8">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <ClientHeader client={client} canEdit={canEdit}
        onDeactivate={() => {
          if (window.confirm(t("clients.deactivate_confirm"))) deactivate.mutate();
        }}
      />

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <TabButton active={tab === "electricity"} onClick={() => setTab("electricity")}>
          {t("clients.tab_electricity")}
        </TabButton>
        <TabButton active={tab === "heat"} onClick={() => setTab("heat")}>
          {t("clients.tab_heat")}
        </TabButton>
      </div>

      <MetersSection clientId={clientId} energyType={tab} canEdit={canEdit} />
      <TariffsSection
        clientId={clientId}
        energyType={tab}
        tariffType={client.tariff_type}
        canEdit={canEdit}
      />
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
        active
          ? "border-brand-navy-500 text-brand-navy-700"
          : "border-transparent text-slate-500 hover:text-slate-700",
      )}
    >
      {children}
    </button>
  );
}

// ---------- Client header (edit name + tariff_type) ----------

function ClientHeader({
  client,
  canEdit,
  onDeactivate,
}: {
  client: Client;
  canEdit: boolean;
  onDeactivate: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ClientUpdate>({
    name: client.name,
    tariff_type: client.tariff_type,
    trader_fee_pct: Number(client.trader_fee_pct),
  });

  const save = useMutation({
    mutationFn: () => clientsApi.update(client.id, form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      setEditing(false);
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{client.name}</CardTitle>
          {canEdit && (
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button onClick={() => save.mutate()} disabled={save.isPending} size="sm">
                    {save.isPending ? t("common.saving") : t("common.save")}
                  </Button>
                  <Button variant="outline" size="sm"
                    onClick={() => {
                      setEditing(false);
                      setForm({
                        name: client.name,
                        tariff_type: client.tariff_type,
                        trader_fee_pct: Number(client.trader_fee_pct),
                      });
                    }}>
                    {t("common.cancel")}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                    {t("common.edit")}
                  </Button>
                  {client.is_active && (
                    <Button variant="destructive" size="sm" onClick={onDeactivate}>
                      {t("clients.deactivate")}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <Label className="text-xs text-slate-500">{t("clients.name")}</Label>
            {editing ? (
              <Input value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            ) : (
              <div className="text-slate-800">{client.name}</div>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-500">{t("clients.code")}</Label>
            <div className="text-slate-800 font-mono text-sm">{client.code}</div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">{t("clients.tariff_type")}</Label>
            {editing ? (
              <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                value={form.tariff_type ?? "fixed"}
                onChange={(e) => setForm({ ...form, tariff_type: e.target.value as ClientUpdate["tariff_type"] })}>
                <option value="fixed">{t("clients.tariff_type_fixed")}</option>
                <option value="rdn_dynamic">{t("clients.tariff_type_rdn_dynamic")}</option>
              </select>
            ) : (
              <div className="text-slate-800">{t(`clients.tariff_type_${client.tariff_type}`)}</div>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-500">{t("clients.trader_fee_pct")}</Label>
            {editing && form.tariff_type === "rdn_dynamic" ? (
              <Input type="number" step="0.01" value={form.trader_fee_pct ?? 0}
                onChange={(e) => setForm({ ...form, trader_fee_pct: Number(e.target.value) })} />
            ) : (
              <div className="text-slate-800">{client.trader_fee_pct}%</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
